import { Box } from '@chakra-ui/layout'
import { useToast } from '@chakra-ui/react'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import {
    forwardRef,
    ForwardRefRenderFunction,
    memo,
    useEffect,
    useImperativeHandle,
    useState
} from 'react'
import { removeEmptyValues } from 'utils/removeEmptyValues'

import { getDataBilling } from 'services/billing'
import formatter from 'utils/formatter'
import {
    BillingItem,
    BillingItemKey,
    BillingQueryDTO,
    FormFilter,
    IModeTableRef
} from '../interface'
import { defaultLoader } from './constants'

interface Props {
    filter: FormFilter
    setLoadingFilter: (loading: boolean) => void
}

const ModeTableFC: ForwardRefRenderFunction<IModeTableRef, Props> = (
    { filter, setLoadingFilter },
    refModeTable
) => {
    const toast = useToast()
    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(1)
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const { currentInstitution, currentAgent } = useInstitutionsContext()

    useImperativeHandle(refModeTable, () => ({
        resetPage() {
            setPage(1)
        }
    }))

    const [table, setTable] = useState<TableBuildProps<BillingItemKey>>({
        header: [
            {
                name: 'razaoSocialAgente',
                text: 'Agente Financeiro',
                position: 0
            },
            {
                name: 'cnpjAgente',
                text: 'CNPJ Agente',
                position: 1
            },
            {
                name: 'descricao',
                text: 'UF',
                position: 2
            },
            {
                name: 'quantidadeTotal',
                text: 'Total de Registros',
                position: 3
            },
            {
                name: 'totalValorRegistradora',
                text: 'Valor Total Registradora',
                position: 4
            },
            {
                name: 'totalTaxaRegistro',
                text: 'Valor Total Taxa Registro',
                position: 5
            }
        ]
    })
    const buildPayload = (): BillingQueryDTO => {
        const payload = removeEmptyValues({
            pagina: page,
            limite: limit,
            cnpjAgente: currentAgent?.cnpj ?? '',
            instituicaoFinanceira: currentInstitution?.id ?? '',
            ...filter,
            periodoInicial: filter?.periodoInicial
                ? formatter.formatDateToPayload(filter?.periodoInicial)
                : '',
            periodoFinal: filter?.periodoFinal
                ? formatter.formatDateToPayload(filter?.periodoFinal)
                : ''
        })

        return payload
    }

    const buildTable = (data: BillingItem[]) => {
        return data.map(item => ({
            ...item,
            cnpjAgente: item.cnpjAgente ? formatter.cnpj(item.cnpjAgente) : '-',
            totalTaxaRegistro: formatter.moeda(item.totalTaxaRegistro),
            totalValorRegistradora: formatter.moeda(item.totalValorRegistradora)
        }))
    }

    async function fetchListBilling() {
        setLoading(true)
        setLoadingFilter(true)
        try {
            const payload = buildPayload()
            const { data: billings, headers } = await getDataBilling(payload)

            setTotalPages(+headers['total-pages'])
            setTable(old => ({
                ...old,
                body: buildTable(billings)
            }))
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoading(false)
            setLoadingFilter(false)
        }
    }

    const handlePage = (value: number) => {
        if (page !== value) {
            setPage(value)
        }
    }

    const handleLimit = (value: number) => {
        if (limit !== value) {
            setLimit(value)
            setPage(1)
        }
    }
    useEffect(() => {
        if (currentInstitution?.id !== undefined) {
            fetchListBilling()
        }
    }, [page, limit, currentInstitution?.id, currentAgent?.cnpj, filter])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                loading={{
                    status: loading,
                    loader: defaultLoader
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            ></TableBuild>
        </Box>
    )
}

export const ModeTable = memo(forwardRef(ModeTableFC))
